import type {
    ArgumentsSchema,
    AutomationAction,
    AvailableFieldBlockInfo,
    AvailableFilterBlockInfo,
    DataSourceAbstract,
    DateVariableValue,
    EVENT_VARIABLE_TYPE,
    EVENT_VARIABLE_VALUE,
    Field,
    FieldType,
    FilterBlockItemConfig,
    FlowAction,
    IndependentAction,
    LOOP_VARIABLE_VALUE,
    PageVariableSchema,
    StateSchema,
    TypeInstanceMap,
    VariableADTvalue,
    VariableFieldADTValue,
    ViewType
} from '@lighthouse/core'
import {
    ActionHandleEventType,
    BlockType,
    DataSourceType,
    DEVICE_ID_VALUE,
    FilterWay,
    ImageVariableValue,
    VariableType
} from '@lighthouse/core'
import { JSONContent } from '@tiptap/core'
import { find, reduce } from 'rambda'
import { type MakeADT } from 'ts-adt/MakeADT'

import {
    ActionIconMap,
    ActionNameMap,
    BlockTypeIconMap,
    defaultFormat,
    FIELD_BLOCK_ICON_MAP,
    FieldIconTypeMap,
    FilterWayToInnerTypeMap,
    innerTypeToFieldTypeIconMap,
    inputTypeToInnerTypeMap,
    loopVariableInnerTypeMap,
    loopVariableNameMap,
    nodeTypeIconMap,
    OUTPUT_ARGS_NODES,
    OUTPUT_FLOW_DATA_NODES,
    OUTPUT_SPECIFIC_DATA_NODES,
    OutputSpecificDataNodeList,
    USER_DATASOURCE,
    variableSystemNameMap,
    VIEW_BLOCK_NAME_MAP,
    VIEW_ICON_MAP
} from '../../constants'
import type {
    AggregateResultField,
    AggregatorNode,
    EndFlowNode,
    FlowNode,
    HandleSubProcessActionConfig,
    VariableTriggerConfig,
    Workflow
} from '../../types'
import { AggregatorNodeType, EndNodeType } from '../../types'
import {
    getActionDataSourceId,
    getFieldIcon,
    getIsMatchInnerTypeByOperator,
    getNodeDataSourceId,
    getRealField,
    getTableIcon,
    geyAggregatorNodeIcon
} from '../../utils'
import { collectEventTypeName, CURRENT_PAGE, DEFAULT_VALUE_VARIABLE, eventOption, eventTypeIcon } from './constant'
import type { VariableTree2 } from './types'

export const getEmptyFieldVariableValue = function (type: FieldType): VariableFieldADTValue {
    switch (type) {
        case 'number':
        case 'text':
        case 'url':
        case 'phoneNumber':
        case 'email':
        case 'id':
        case 'date': {
            return { type, value: '', format: defaultFormat }
        }
        case 'select':
        case 'person':
        case 'department':
        case 'role':
        case 'video':
        case 'file': {
            return { type, value: [] }
        }
        default: {
            return { type: 'text', value: '' }
        }
    }
}

export const getMultipleValue = function (list: string[], current: string) {
    return list.includes(current) ? list.filter(item => item !== current) : [...list, current]
}

export const getVariableValue = function (option: VariableTree2, path: string[]): VariableADTvalue {
    switch (option.type) {
        case VariableType.ARG_VARIABLE: {
            return { type: option.type, argVariable: { nodeId: path[path.length - 2], argId: path[path.length - 1] } }
        }
        case VariableType.UPSTREAM: {
            return { type: option.type, upstreamVariable: { nodeId: path[path.length - 2], fieldId: path[path.length - 1] } }
        }
        case VariableType.VARIABLE: {
            return { type: option.type, fieldVariable: { dsId: path[path.length - 2], fieldId: path[path.length - 1] } }
        }
        case VariableType.PAGE_DATA_QUERY: {
            return {
                type: option.type,
                pageDataQueryVariable: { id: path[path.length - 2], itemId: path[path.length - 1] }
            }
        }
        case VariableType.ROUTE_PATH: {
            return { type: option.type, routePathVariable: { id: path[path.length - 1] } }
        }
        case VariableType.ROUTE_QUERY: {
            return { type: option.type, routeQueryVariable: { id: path[path.length - 1] } }
        }
        case VariableType.PAGE_STATE: {
            return { type: option.type, pageStateVariable: { id: path[path.length - 1] } }
        }
        case VariableType.FIELD_ID: {
            return { type: option.type, fieldIdVariable: { fieldId: path[path.length - 1] } }
        }
        case VariableType.USER: {
            return { type: option.type, userVariable: { fieldId: path[path.length - 1] } }
        }
        case VariableType.SYSTEM: {
            return { type: option.type, systemVariable: { value: path[path.length - 1] as DateVariableValue } }
        }
        case VariableType.INPUT: {
            return { type: option.type, inputVariable: { blockId: path[path.length - 1], scope: option.scope } }
        }
        case VariableType.FORM: {
            return { type: option.type, formVariable: { fieldId: path[path.length - 1] } }
        }
        case VariableType.VIEW: {
            return { type: option.type, viewVariable: { fieldId: path[path.length - 1] } }
        }
        case VariableType.PAGE_LINK: {
            return { type: option.type, pageLinkVariable: { value: CURRENT_PAGE } }
        }
        case VariableType.DEVICE_ID: {
            return { type: option.type, deviceIdVariable: { value: DEVICE_ID_VALUE } }
        }
        case VariableType.FILTER: {
            return {
                type: option.type,
                filterVariable: { blockId: path[path.length - 2], scope: option.scope, itemId: path[path.length - 1] }
            }
        }
        case VariableType.EVENT: {
            return {
                type: option.type,
                eventVariable: { value: path[path.length - 1] as EVENT_VARIABLE_VALUE }
            }
        }
        case VariableType.LOOP: {
            return {
                type: option.type,
                loopVariable: { value: path[path.length - 1] as LOOP_VARIABLE_VALUE }
            }
        }
        case VariableType.FORMULA: {
            return {
                type: option.type,
                formulaVariable: { value: {} }
            }
        }
        case VariableType.IMAGE: {
            return {
                type: option.type,
                imageVariable: { dataKey: path[path.length - 1] as ImageVariableValue }
            }
        }
        default: {
            return DEFAULT_VALUE_VARIABLE
        }
    }
}

export const isVariableItemActive = function (option: VariableTree2, value?: VariableADTvalue): boolean {
    if (!value) {
        return false
    }
    switch (value.type) {
        case VariableType.ARG_VARIABLE: {
            return (
                Boolean(value.argVariable) &&
                (option.value === value.argVariable?.nodeId || option.value === value.argVariable?.argId) &&
                option.type === value.type
            )
        }
        case VariableType.UPSTREAM: {
            return (
                Boolean(value.upstreamVariable) &&
                (option.value === value.upstreamVariable?.nodeId || option.value === value.upstreamVariable?.fieldId) &&
                option.type === value.type
            )
        }
        case VariableType.VARIABLE: {
            return (
                Boolean(value.fieldVariable) &&
                (option.value === value.fieldVariable?.dsId || option.value === value.fieldVariable?.fieldId) &&
                option.value === value.type
            )
        }

        case VariableType.PAGE_DATA_QUERY: {
            return (
                Boolean(value.pageDataQueryVariable) &&
                (option.value === value.pageDataQueryVariable?.id || option.value === value.pageDataQueryVariable?.itemId) &&
                option.type === value.type
            )
        }
        case VariableType.ROUTE_PATH: {
            return Boolean(value.routePathVariable) && option.value === value.routePathVariable?.id && option.type === value.type
        }
        case VariableType.ROUTE_QUERY: {
            return Boolean(value.routeQueryVariable) && option.value === value.routeQueryVariable?.id && option.type === value.type
        }

        case VariableType.FIELD_ID: {
            return Boolean(value.fieldIdVariable) && option.value === value.fieldIdVariable?.fieldId && option.type === value.type
        }
        case VariableType.USER: {
            return (
                Boolean(value.userVariable) &&
                (option.value === USER_DATASOURCE || option.value === value.userVariable?.fieldId) &&
                option.type === value.type
            )
        }
        case VariableType.INPUT: {
            return (
                Boolean(value.inputVariable) &&
                option.value === value.inputVariable?.blockId &&
                option.scope === value.inputVariable.scope &&
                option.type === value.type
            )
        }
        case VariableType.SYSTEM: {
            return Boolean(value.systemVariable) && option.value === value.systemVariable?.value && option.type === value.type
        }
        case VariableType.FORM: {
            return Boolean(value.formVariable) && option.value === value.formVariable?.fieldId && option.type === value.type
        }
        case VariableType.VIEW: {
            return Boolean(value.viewVariable) && option.value === value.viewVariable?.fieldId && option.type === value.type
        }
        case VariableType.PAGE_LINK: {
            return Boolean(value.pageLinkVariable) && option.value === value.pageLinkVariable?.value && option.type === value.type
        }
        case VariableType.DEVICE_ID: {
            return Boolean(value.deviceIdVariable) && option.value === value.deviceIdVariable?.value && option.type === value.type
        }
        case VariableType.FILTER: {
            return Boolean(value.filterVariable) && option.value === value.filterVariable?.blockId && option.type === value.type
        }
        case VariableType.SELECT_DATASOURCE: {
            return (
                Boolean(value.selectDataSourceVariable) &&
                option.value === value.selectDataSourceVariable?.filter &&
                option.type === value.type
            )
        }
        default: {
            return false
        }
    }
}

/** 当前数据表的 option */
export const getDefaultDataSourceOptions = function (
    dataSource?: DataSourceAbstract,
    validateFieldType?: (innerType: TypeInstanceMap) => boolean
): VariableTree2 | undefined {
    if (!dataSource) {
        return
    }
    return {
        type: VariableType.FIELD_ID,
        icon: 'BlockDatabase',
        label: dataSource.name,
        value: dataSource.id,
        searchable: true,
        children: reduce<Field, VariableTree2[]>(
            (preVal, field) => {
                if (!validateFieldType || validateFieldType(field.innerType)) {
                    const icon = getFieldIcon(field.id, field.type, field.innerType)
                    return [
                        ...preVal,
                        {
                            type: VariableType.FIELD_ID,
                            icon,
                            label: field.name,
                            value: field.id,
                            description: dataSource.type === DataSourceType.joinDataSource ? field.dsName : undefined,
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        }
                    ]
                }
                return preVal
            },
            [],
            Object.values(dataSource.schema)
        )
    }
}

/** 页面链接 option */
export const PAGE_LINK_OPTION: VariableTree2 = {
    type: VariableType.PAGE_LINK,
    icon: 'PropertyLink',
    label: '页面链接',
    value: CURRENT_PAGE
}

/** 设备ID option */
export const DEVICE_ID_OPTION: VariableTree2 = {
    type: VariableType.DEVICE_ID,
    icon: 'PropertyPhone',
    label: '设备ID',
    value: DEVICE_ID_VALUE,
    fieldInnerType: 'TEXT'
}

type PageVariableOptionsParams = {
    pageVariables: PageVariableSchema[] | undefined
    pagePathParameter: ArgumentsSchema[] | undefined
    pageQueryParameter: ArgumentsSchema[] | undefined
    pageStateParameter: StateSchema[] | undefined
    dataSourceList: DataSourceAbstract[]
    fieldFilter?: (field: Field | TypeInstanceMap) => boolean
    includeDeviceId?: boolean
}
/** 获取页面数据options */
export const getPageParameterAndVariableOptions = ({
    pageVariables,
    pagePathParameter,
    pageQueryParameter,
    pageStateParameter,
    dataSourceList,
    fieldFilter = () => true,
    includeDeviceId = false
}: PageVariableOptionsParams): VariableTree2[] => {
    const data: VariableTree2[] = []

    if (includeDeviceId) {
        data.push(DEVICE_ID_OPTION)
    }

    if (pagePathParameter) {
        data.push({
            type: VariableType.ROUTE_PATH,
            icon: 'SlashSquare',
            label: '路径参数',
            value: VariableType.ROUTE_PATH,
            searchable: true,
            children: pagePathParameter
                .filter(item => fieldFilter(item.innerType))
                .map(item => ({
                    type: VariableType.ROUTE_PATH,
                    label: item.name,
                    value: item.id,
                    fieldInnerType: item.innerType
                }))
        })
    }

    if (pageQueryParameter) {
        data.push({
            type: VariableType.ROUTE_QUERY,
            icon: 'QuestionSquare',
            label: '查询参数',
            value: VariableType.ROUTE_QUERY,
            searchable: true,
            children: pageQueryParameter
                .filter(item => fieldFilter(item.innerType))
                .map(item => ({
                    type: VariableType.ROUTE_QUERY,
                    label: item.name,
                    value: item.id,
                    fieldInnerType: item.innerType
                }))
        })
    }

    if (pageVariables) {
        data.push({
            type: VariableType.PAGE_DATA_QUERY,
            icon: 'PageDatabase',
            label: '页面数据',
            value: VariableType.PAGE_DATA_QUERY,
            searchable: true,
            children: pageVariables.map(item => {
                const ds = dataSourceList.find(d => d.id === item.dsId)
                const children = ds
                    ? item.items
                          .filter(f => {
                              const field = ds.schema[f.fieldId]
                              return field && fieldFilter(field)
                          })
                          .map(f => {
                              const field = ds.schema[f.fieldId]
                              return {
                                  type: VariableType.PAGE_DATA_QUERY,
                                  icon: getFieldIcon(field.id, field.type, field.innerType),
                                  label: f.name,
                                  value: f.id,
                                  fieldType: field.type,
                                  fieldInnerType: field.innerType
                              }
                          })
                    : []

                return {
                    type: VariableType.PAGE_DATA_QUERY,
                    label: item.name,
                    value: item.id,
                    searchable: true,
                    children
                }
            })
        })
    }
    if (pageStateParameter) {
        data.push({
            type: VariableType.PAGE_STATE,
            icon: 'PageVariable',
            label: '页面变量',
            value: VariableType.PAGE_STATE,
            searchable: true,
            children: pageStateParameter
                .filter(item => fieldFilter(item.innerType))
                .map(item => ({
                    type: VariableType.PAGE_STATE,
                    label: item.name,
                    value: item.id,
                    fieldInnerType: item.innerType
                }))
        })
    }

    return data
}

type UpstreamNodesOptionsParams = {
    workflowList: Workflow[]
    upstreamNodes: FlowNode[] | undefined
    allUpstreamNodes: FlowNode[] | undefined
    dataSourceList: DataSourceAbstract[]
    filter?: (arg: Field | TypeInstanceMap) => boolean
}
/** 获取上游节点 options */
export const getUpstreamNodesOptions = ({
    workflowList,
    upstreamNodes,
    allUpstreamNodes,
    dataSourceList,
    filter
}: UpstreamNodesOptionsParams): VariableTree2[] => {
    if (!upstreamNodes || !allUpstreamNodes) {
        return []
    }

    return upstreamNodes.reduce<VariableTree2[]>((prev, curr) => {
        if (OUTPUT_ARGS_NODES.has(curr.data.nodeType)) {
            let args: ArgumentsSchema[] = []

            if (OUTPUT_SPECIFIC_DATA_NODES.has(curr.data.nodeType)) {
                const nodeType = curr.data.nodeType as keyof typeof OutputSpecificDataNodeList
                args = OutputSpecificDataNodeList[nodeType] || []
            } else if (OUTPUT_FLOW_DATA_NODES.has(curr.data.nodeType)) {
                const handleSubProcessActionConfig = curr.data.config as HandleSubProcessActionConfig
                const subProcessId = handleSubProcessActionConfig?.subProcessId
                const subProcess = find(item => item.id === subProcessId, workflowList)
                if (!subProcess) {
                    return prev
                }
                const nodeList = subProcess.content?.nodes || []
                const endNode = find(item => item.data.nodeType === 'END', nodeList) as EndFlowNode | undefined
                if (!endNode) {
                    return prev
                }
                const outputArgs = endNode.data.config?.outputArgs || []
                args = outputArgs.map<ArgumentsSchema>(item => ({
                    id: item.id,
                    name: item.name,
                    innerType: item.innerType
                }))
            } else {
                const config = curr.data.config as VariableTriggerConfig
                args = config?.args || []
            }

            const option: VariableTree2 = {
                type: VariableType.ARG_VARIABLE,
                icon: nodeTypeIconMap[curr.data.nodeType],
                label: curr.data.name,
                value: curr.id,
                searchable: true,
                children: args.reduce<VariableTree2[]>((total, arg) => {
                    if (!filter || filter(arg.innerType)) {
                        return [
                            ...total,
                            {
                                type: VariableType.ARG_VARIABLE,
                                icon: innerTypeToFieldTypeIconMap[arg.innerType],
                                label: arg.name,
                                value: arg.id,
                                fieldInnerType: arg.innerType
                            }
                        ]
                    }

                    return total
                }, [])
            }

            return [...prev, option]
        }

        const dsId = getNodeDataSourceId(curr, allUpstreamNodes)
        if (!dsId) {
            return prev
        }
        const ds = dataSourceList.find(item => item.id === dsId)
        if (!ds) {
            return prev
        }

        const option: VariableTree2 = {
            type: VariableType.UPSTREAM,
            icon: nodeTypeIconMap[curr.data.nodeType],
            label: curr.data.name,
            value: curr.id,
            dsId: ds.id,
            description: ds.name,
            searchable: true,
            children: Object.values(ds.schema).reduce<VariableTree2[]>((total, field) => {
                if (!filter || filter(field)) {
                    total.push({
                        type: VariableType.UPSTREAM,
                        icon: getFieldIcon(field.id, field.type, field.innerType),
                        label: field.name,
                        value: field.id,
                        dsId: ds.id,
                        fieldType: field.type,
                        fieldInnerType: field.innerType
                    })
                    return total
                }
                return total
            }, [])
        }

        return [...prev, option]
    }, [])
}

export const getFlowIdByAction = (action: FlowAction | AutomationAction) => {
    if (action.type === ActionHandleEventType.ACTION) {
        return action.data.flow.flowId
    }
    return action.data.automation.flowId
}

type UpstreamActionsOptionsParams = {
    workflowList: Workflow[]
    allParentActions: IndependentAction[]
    dataSourceList: DataSourceAbstract[]
    filter?: (arg: Field | TypeInstanceMap) => boolean
}
/** 获取上游动作 options */
export const getUpstreamActionsOptions = ({
    workflowList,
    allParentActions,
    dataSourceList,
    filter
}: UpstreamActionsOptionsParams): VariableTree2[] => {
    if (!allParentActions) {
        return []
    }
    return allParentActions.reduce<VariableTree2[]>((prev, curr) => {
        if (curr.type === ActionHandleEventType.ACTION || curr.type === ActionHandleEventType.AUTOMATION) {
            const flowId = getFlowIdByAction(curr)
            const workflow = find(item => item.id === flowId, workflowList)
            if (!workflow) {
                return prev
            }
            const endNode = find(item => item.data.nodeType === EndNodeType.END, workflow.content.nodes) as EndFlowNode | undefined
            if (!endNode) {
                return prev
            }
            const outputArgs = endNode.data.config?.outputArgs || []
            const args = outputArgs.map<ArgumentsSchema>(item => ({
                id: item.id,
                name: item.name,
                innerType: item.innerType
            }))
            const option: VariableTree2 = {
                type: VariableType.UPSTREAM,
                icon: ActionIconMap[curr.type],
                label: workflow.name,
                value: curr.id,
                searchable: true,
                children: args.reduce<VariableTree2[]>((total, arg) => {
                    if (!filter || filter(arg.innerType)) {
                        return [
                            ...total,
                            {
                                type: VariableType.ARG_VARIABLE,
                                icon: innerTypeToFieldTypeIconMap[arg.innerType],
                                label: arg.name,
                                value: arg.id,
                                fieldInnerType: arg.innerType
                            }
                        ]
                    }
                    return total
                }, [])
            }
            return [...prev, option]
        }

        const dsId = getActionDataSourceId(curr, allParentActions)
        if (!dsId) {
            return prev
        }
        const ds = dataSourceList.find(item => item.id === dsId)
        if (!ds) {
            return prev
        }

        const option: VariableTree2 = {
            type: VariableType.UPSTREAM,
            icon: ActionIconMap[curr.type],
            label: ActionNameMap[curr.type],
            value: curr.id,
            dsId: ds.id,
            description: ds.name,
            searchable: true,
            children: Object.values(ds.schema).reduce<VariableTree2[]>((total, field) => {
                if (!filter || filter(field)) {
                    total.push({
                        type: VariableType.UPSTREAM,
                        icon: getFieldIcon(field.id, field.type, field.innerType),
                        label: field.name,
                        value: field.id,
                        dsId: ds.id,
                        fieldType: field.type,
                        fieldInnerType: field.innerType
                    })
                    return total
                }
                return total
            }, [])
        }

        return [...prev, option]
    }, [])
}

export type GetVariableOptionsADT = MakeADT<
    'type',
    {
        upstream: UpstreamNodesOptionsParams
        page: PageVariableOptionsParams
    }
>

const getAggregatorUpstreamDescription = (node: AggregatorNode, dataSourceList: DataSourceAbstract[]) => {
    if (node?.type === AggregatorNodeType.DATA_INPUT) {
        const { dsId } = node.data.config
        const dataSource = find(item => item.id === dsId, dataSourceList)
        return dataSource?.name
    }
    return ''
}

// 获取聚合表上游节点
export const getAggregatorUpstreamOption = ({
    aggregateNodeList,
    dataSourceList,
    validateFieldType
}: {
    aggregateNodeList: AggregatorNode[]
    dataSourceList: DataSourceAbstract[]
    validateFieldType?: ((fieldInnerType: TypeInstanceMap) => boolean) | undefined
}): VariableTree2[] => {
    return aggregateNodeList.reduce<VariableTree2[]>((preVal, node) => {
        const {
            id,
            data: {
                result,
                config: { name }
            }
        } = node
        const fieldList = result?.fieldList || []
        const description = getAggregatorUpstreamDescription(node, dataSourceList)
        const dsOption: VariableTree2 = {
            type: VariableType.UPSTREAM,
            icon: geyAggregatorNodeIcon(node),
            label: name,
            value: id,
            description,
            searchable: true,
            children: reduce<AggregateResultField, VariableTree2[]>(
                (preVal, field) => {
                    if (!validateFieldType) {
                        preVal.push({
                            type: VariableType.UPSTREAM,
                            icon: getFieldIcon(field.id, field.type, field.innerType),
                            label: field.name,
                            value: field.id,
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        })
                        return preVal
                    }
                    if (!field.innerType) {
                        return preVal
                    }
                    if (validateFieldType(field.innerType)) {
                        preVal.push({
                            type: VariableType.UPSTREAM,
                            icon: getFieldIcon(field.id, field.type, field.innerType),
                            label: field.name,
                            value: field.id,
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        })
                        return preVal
                    }

                    return preVal
                },
                [],
                fieldList
            )
        }
        return [...preVal, dsOption]
    }, [])
}

// 获取连接表上游连接的数据源
export const getJoinOption = ({
    primaryDsId,
    dataSourceList,
    validateField
}: {
    primaryDsId: string
    dataSourceList: DataSourceAbstract[]
    validateField?: ((field: Field) => boolean) | undefined
}): VariableTree2[] => {
    return dataSourceList.reduce<VariableTree2[]>((preVal, ds) => {
        const { id, schema } = ds
        const fieldList = Object.values(schema)
        const dsOption: VariableTree2 = {
            type: VariableType.VARIABLE,
            icon: getTableIcon(ds),
            label: ds.name,
            value: id,
            description: primaryDsId === id ? '主表' : '连接表',
            searchable: true,
            children: reduce<Field, VariableTree2[]>(
                (preVal, field) => {
                    if (!validateField) {
                        preVal.push({
                            type: VariableType.VARIABLE,
                            label: field.name,
                            value: field.id,
                            icon: getFieldIcon(field.id, field.type, field.innerType),
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        })
                        return preVal
                    }
                    if (!field.innerType) {
                        return preVal
                    }
                    if (validateField(field)) {
                        preVal.push({
                            type: VariableType.VARIABLE,
                            icon: getFieldIcon(field.id, field.type, field.innerType),
                            label: field.name,
                            value: field.id,
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        })
                        return preVal
                    }

                    return preVal
                },
                [],
                fieldList
            )
        }
        return [...preVal, dsOption]
    }, [])
}

/** 获取当前用户 option */
export const getUserDatasourceOptions = function (params: {
    dataSource?: DataSourceAbstract
    validateFieldType?: (field: Field) => boolean
}): VariableTree2 | undefined {
    const { dataSource, validateFieldType } = params
    if (!dataSource) {
        return
    }
    return {
        type: VariableType.USER,
        icon: getTableIcon(dataSource),
        label: '当前登录用户数据',
        value: dataSource.id,
        searchable: true,
        children: reduce<Field, VariableTree2[]>(
            (preVal, field) => {
                if (!validateFieldType || validateFieldType(field)) {
                    return [
                        ...preVal,
                        {
                            type: VariableType.USER,
                            icon: getFieldIcon(field.id, field.type, field.innerType),
                            label: field.name,
                            value: field.id,
                            fieldType: field.type,
                            fieldInnerType: field.innerType
                        }
                    ]
                }
                return preVal
            },
            [],
            Object.values(dataSource.schema)
        )
    }
}

/** 获取日期变量的 option */
export const getDateVariableOption = function (data: DateVariableValue[]): VariableTree2 {
    return {
        type: VariableType.SYSTEM,
        label: '常用日期',
        value: '',
        icon: FieldIconTypeMap['date'],
        children: data.map(item => ({
            type: VariableType.SYSTEM,
            label: variableSystemNameMap[item],
            value: item,
            fieldInnerType: 'DATE'
        }))
    }
}

/** 获取事件变量的 option */
export const getEventOption = function (type: EVENT_VARIABLE_TYPE, innerType: TypeInstanceMap): VariableTree2 | undefined {
    if (innerType !== 'TEXT') {
        return
    }
    return {
        type: VariableType.EVENT,
        icon: eventTypeIcon[type],
        label: collectEventTypeName[type],
        value: type,
        searchable: true,
        children: eventOption[type]
    }
}

/** 获取输入组件变量的 option */
export const getInputVariableOption = function (
    fieldBlocks: AvailableFieldBlockInfo[],
    dataSourceList: DataSourceAbstract[],
    event?: {
        validateFieldType?: (innerType: TypeInstanceMap) => boolean
        onMouseEnter?: (blockId: string) => void
        onMouseLeave?: (blockId: string) => void
    }
): VariableTree2 | undefined {
    const { validateFieldType, onMouseEnter, onMouseLeave } = event || {}
    return {
        type: VariableType.INPUT,
        icon: 'InputIcon',
        label: '输入框',
        value: 'INPUT_VARIABLE',
        searchable: true,
        children: reduce<AvailableFieldBlockInfo, VariableTree2[]>(
            (preVal, fieldBlockInfo) => {
                // const innerType = fieldBlock.type === 'field' ? fieldBlock.innerType : 'BOOL'
                if (fieldBlockInfo.block.type === BlockType.field) {
                    const {
                        block: {
                            id,
                            title,
                            config: { inputType, fieldPointer }
                        },
                        formContainer,
                        scope
                    } = fieldBlockInfo
                    const dsId = formContainer?.config?.pointer
                    const ds = find(item => item.id === dsId, dataSourceList)
                    const field = ds?.schema[fieldPointer || '']
                    const innerType = field?.innerType || inputTypeToInnerTypeMap[inputType]
                    if (validateFieldType && !validateFieldType(innerType)) {
                        return preVal
                    }
                    return [
                        ...preVal,
                        {
                            type: VariableType.INPUT,
                            icon: FIELD_BLOCK_ICON_MAP[inputType],
                            label: title ?? '',
                            value: id,
                            fieldInnerType: innerType,
                            scope,
                            onMouseEnter: () => onMouseEnter?.(id),
                            onMouseLeave: () => onMouseLeave?.(id)
                        }
                    ]
                }
                if (fieldBlockInfo.block.type === BlockType.onOff) {
                    const {
                        block: { id, type, title },
                        scope
                    } = fieldBlockInfo
                    if (validateFieldType && !validateFieldType('BOOL')) {
                        return preVal
                    }
                    return [
                        ...preVal,
                        {
                            type: VariableType.INPUT,
                            icon: BlockTypeIconMap[type],
                            label: title ?? '',
                            value: id,
                            fieldInnerType: 'BOOL',
                            scope,
                            onMouseEnter: () => onMouseEnter?.(id),
                            onMouseLeave: () => onMouseLeave?.(id)
                        }
                    ]
                }
                return preVal
            },
            [],
            fieldBlocks
        )
    }
}

/** 获取筛选控制器组件变量的 option */
export const getFilterControllerVariableOption = function (
    filterBlocks: AvailableFilterBlockInfo[],
    // dataSourceList: DataSourceAbstract[],
    event?: {
        validateFieldType?: (innerType: TypeInstanceMap) => boolean
        onMouseEnter?: (blockId: string) => void
        onMouseLeave?: (blockId: string) => void
    }
): VariableTree2 | undefined {
    const { validateFieldType, onMouseEnter, onMouseLeave } = event || {}
    const allowFilterWay = new Set([
        FilterWay.boolFilter,
        FilterWay.textFilter,
        FilterWay.personFilter,
        FilterWay.selectFilter,
        FilterWay.cascadeFilter
    ])
    return {
        type: VariableType.FILTER,
        icon: 'Filter',
        label: '筛选控制器',
        value: 'FILTER_VARIABLE',
        searchable: true,
        children: filterBlocks.map(filterBlockInfo => {
            const { block, scope } = filterBlockInfo
            return {
                type: VariableType.FILTER,
                icon: 'Filter',
                label: block.title,
                value: block.id,
                scope,
                onMouseEnter: () => onMouseEnter?.(block.id),
                onMouseLeave: () => onMouseLeave?.(block.id),
                children: reduce<FilterBlockItemConfig, VariableTree2[]>(
                    (preVal, item) => {
                        const { filterWay, title, id } = item
                        const innerType = FilterWayToInnerTypeMap[filterWay]

                        if (!allowFilterWay.has(filterWay)) {
                            return preVal
                        }
                        if (!validateFieldType || validateFieldType(innerType)) {
                            return [
                                ...preVal,
                                {
                                    type: VariableType.FILTER,
                                    label: title,
                                    value: id,
                                    fieldInnerType: innerType,
                                    scope
                                }
                            ]
                        }
                        return preVal
                    },
                    [],
                    block.config?.filterItems || []
                )
            }
        })
    }
}

/** 视图的变量 option */
export const getViewVariableOption = (params: {
    dataSource?: DataSourceAbstract
    viewType?: ViewType
    extraFields?: Field[]
    validateFieldType?: (field: Field) => boolean
}): VariableTree2 | undefined => {
    const { dataSource, viewType, extraFields, validateFieldType } = params
    if (!dataSource || !viewType) {
        return
    }
    const toOption = (field: Field) => {
        const realField = getRealField(field)
        if (!realField) {
            return
        }
        if (validateFieldType && !validateFieldType(realField)) {
            return
        }
        return {
            type: VariableType.VIEW,
            label: realField.name,
            value: realField.id,
            icon: getFieldIcon(realField.id, realField.type, realField.innerType),
            fieldType: field.type,
            fieldInnerType: field.innerType
        } as VariableTree2
    }
    const children = Object.values(dataSource.schema).reduce<VariableTree2[]>((prev, field) => {
        const option = toOption(field)
        if (option) {
            prev.push(option)
        }
        return prev
    }, [])
    extraFields?.forEach(field => {
        if (children.some(item => item.value === field.id)) {
            return
        }
        const option = toOption(field)
        if (option) {
            children.push(option)
        }
    })
    return {
        type: VariableType.VIEW,
        label: viewType === 'custom' ? VIEW_BLOCK_NAME_MAP[viewType] : `${VIEW_BLOCK_NAME_MAP[viewType]}视图`,
        value: dataSource.id,
        icon: VIEW_ICON_MAP[viewType],
        searchable: true,
        children
    }
}

/** 获取表单 option */
export const getFormDatasourceOptions = function (params: {
    dataSource?: DataSourceAbstract
    validateFieldType?: (field: Field) => boolean
}): VariableTree2 | undefined {
    const { dataSource, validateFieldType } = params
    if (!dataSource) {
        return
    }
    const fieldList = Object.entries(dataSource.schema)

    return {
        type: VariableType.FORM,
        icon: 'LayerFormContainerBlock',
        label: '表单容器',
        value: dataSource.id,
        searchable: true,
        children: reduce<[string, Field], VariableTree2[]>(
            (preVal, [fieldId, field]) => {
                const realField = getRealField(field)
                if (!realField) {
                    return preVal
                }
                if (!validateFieldType || validateFieldType(realField)) {
                    preVal.push({
                        type: VariableType.FORM,
                        icon: getFieldIcon(realField.id, realField.type, realField.innerType),
                        label: realField.name,
                        value: fieldId,
                        fieldType: field.type,
                        fieldInnerType: field.innerType
                    })
                }
                return preVal
            },
            [],
            fieldList
        )
    }
}

export const getFieldsVariableOption = function (params: {
    fields: Field[]
    validateFieldType?: (field: Field) => boolean
}): VariableTree2[] | undefined {
    const { fields, validateFieldType } = params
    if (!fields) {
        return
    }
    return reduce<Field, VariableTree2[]>(
        (preVal, field) => {
            if (!validateFieldType || validateFieldType(field)) {
                return [
                    ...preVal,
                    {
                        type: VariableType.FIELD_ID,
                        label: field.name,
                        value: field.id,
                        icon: getFieldIcon(field.id, field.type, field.innerType),
                        fieldType: field.type,
                        fieldInnerType: field.innerType
                    }
                ]
            }
            return preVal
        },
        [],
        fields
    )
}
/** 获取循环变量的 option */
export const getLoopVariableOption = function (params: {
    name: string
    data: LOOP_VARIABLE_VALUE[]
    filter?: (arg: Field | TypeInstanceMap) => boolean
}): VariableTree2 {
    const { name, data, filter } = params
    return {
        type: VariableType.LOOP,
        label: name,
        value: '',
        icon: nodeTypeIconMap['LOOP_ACTION'],
        children: reduce<LOOP_VARIABLE_VALUE, VariableTree2[]>(
            (preVal, val) => {
                if (!filter || filter(loopVariableInnerTypeMap[val])) {
                    preVal.push({
                        type: VariableType.LOOP,
                        label: loopVariableNameMap[val],
                        value: val,
                        fieldInnerType: loopVariableInnerTypeMap[val]
                    })
                }
                return preVal
            },
            [],
            data
        )
    }
}

export const getImageVariableOption = function (): VariableTree2 {
    return {
        type: VariableType.IMAGE,
        label: '图片',
        value: 'IMAGE_VARIABLE',
        icon: 'Image',
        children: [
            {
                type: VariableType.IMAGE,
                label: '当前图片名称',
                value: ImageVariableValue.IMAGE_NAME,
                fieldInnerType: 'TEXT'
            },
            {
                type: VariableType.IMAGE,
                label: '当前图片链接',
                value: ImageVariableValue.IMAGE_URL,
                fieldInnerType: 'TEXT'
            },
            {
                type: VariableType.IMAGE,
                label: '当前图片序号',
                value: ImageVariableValue.IMAGE_INDEX,
                fieldInnerType: 'NUMBER'
            }
        ]
    }
}

export function deepFilterVariableTree(tree: VariableTree2[], operator: string, innerType: TypeInstanceMap | undefined): VariableTree2[] {
    return tree
        .map(node => {
            if (node.children) {
                return {
                    ...node,
                    children: deepFilterVariableTree(node.children, operator, innerType)
                }
            }

            return node
        })
        .filter(node => {
            if (Object.hasOwn(node, 'fieldInnerType')) {
                return getIsMatchInnerTypeByOperator(innerType, operator, node.fieldInnerType as TypeInstanceMap)
            }

            return true
        })
}
